// Shared utility functions for Analysis components

// Video analysis feature flag - set to true to disable all video analysis
export const IS_VIDEO_DISABLED = false;

// Helper function to generate contextual explanations
export function getMetricExplanation(
  metric: string,
  value: any,
  context: {
    duration?: number;
    wordsPerMinute?: number;
    intent?: string;
    fillerCount?: number;
  }
): { explanation: string; tips: string[]; severity: 'excellent' | 'good' | 'warning' | 'poor' } {
  const { duration = 0, wordsPerMinute = 0, intent = 'general', fillerCount = 0 } = context;

  // Round duration for display purposes
  const roundedDuration = Math.round(duration);

  switch (metric) {
    case 'duration':
      if (intent === 'sales') {
        if (duration < 20) {
          return {
            explanation: `${roundedDuration}s is too short for a sales pitch. You need at least 30-60 seconds to effectively convey your value proposition.`,
            tips: ['Aim for 30-60 seconds for sales pitches', 'Include problem, solution, and benefit', 'Practice extending your key points'],
            severity: 'poor'
          };
        } else if (duration < 30) {
          return {
            explanation: `${roundedDuration}s is quite short for a sales pitch. Consider expanding to 30-60 seconds for better impact.`,
            tips: ['Add more details about benefits', 'Include social proof or examples', 'Practice extending key points'],
            severity: 'warning'
          };
        } else if (duration >= 30 && duration <= 60) {
          return {
            explanation: `${roundedDuration}s is perfect for a sales pitch! This gives you enough time to convey value without losing attention.`,
            tips: ['Maintain this excellent timing', 'Focus on clear, compelling delivery'],
            severity: 'excellent'
          };
        } else if (duration <= 90) {
          return {
            explanation: `${roundedDuration}s is acceptable for a sales pitch, though slightly long. Keep it engaging throughout.`,
            tips: ['Consider tightening your message', 'Ensure every second adds value'],
            severity: 'good'
          };
        }
      } else if (intent === 'elevator-pitch') {
        if (duration <= 30) {
          return {
            explanation: `${roundedDuration}s is perfect for an elevator pitch! Concise and to the point.`,
            tips: ['Maintain this excellent brevity', 'Focus on your unique value'],
            severity: 'excellent'
          };
        } else if (duration <= 60) {
          return {
            explanation: `${roundedDuration}s is good for an elevator pitch. Right at the ideal maximum length.`,
            tips: ['Keep it engaging throughout', 'End with a clear call to action'],
            severity: 'good'
          };
        } else if (duration <= 90) {
          return {
            explanation: `${roundedDuration}s is getting long for an elevator pitch. Consider shortening to maintain attention.`,
            tips: ['Aim for 30-60 seconds', 'Focus on your core message', 'Remove less essential details'],
            severity: 'warning'
          };
        }
      } else if (intent === 'interview') {
        if (duration < 30) {
          return {
            explanation: `${roundedDuration}s is quite brief for interview responses. Most good interview answers need 1-2 minutes to provide sufficient detail.`,
            tips: ['Aim for 60-120 seconds for interview responses', 'Use STAR method (Situation, Task, Action, Result)', 'Provide specific examples'],
            severity: 'warning'
          };
        } else if (duration >= 60 && duration <= 120) {
          return {
            explanation: `${roundedDuration}s is excellent for interview responses! This gives you time to provide detailed, compelling answers.`,
            tips: ['Maintain this ideal length', 'Structure your answers clearly', 'Include specific examples'],
            severity: 'excellent'
          };
        } else if (duration >= 30 && duration < 60) {
          return {
            explanation: `${roundedDuration}s is good for interview responses, though you could elaborate more for stronger impact.`,
            tips: ['Consider adding more specific details', 'Use examples to strengthen your points', 'Expand on your achievements'],
            severity: 'good'
          };
        } else if (duration > 180) {
          return {
            explanation: `${roundedDuration}s is too long for interview responses. Interviewers may lose interest or see you as unfocused.`,
            tips: ['Aim for 60-120 seconds maximum', 'Practice being more concise', 'Focus on most relevant details'],
            severity: 'poor'
          };
        } else if (duration > 120) {
          return {
            explanation: `${roundedDuration}s is getting long for interview responses. Consider being more concise to maintain interviewer engagement.`,
            tips: ['Try to keep responses under 2 minutes', 'Focus on key points', 'Practice summarizing effectively'],
            severity: 'warning'
          };
        }
      } else if (intent === 'presentation') {
        if (duration < 60) {
          return {
            explanation: `${roundedDuration}s is quite short for a presentation segment. Consider if you're covering your topic thoroughly.`,
            tips: ['Expand on key points with examples', 'Add supporting details', "Consider your audience's needs"],
            severity: 'warning'
          };
        } else if (duration >= 120 && duration <= 300) {
          return {
            explanation: `${roundedDuration}s is excellent for a presentation segment! Good balance of depth and audience attention.`,
            tips: ['Maintain this engaging length', 'Use visual aids effectively', 'Keep audience engaged throughout'],
            severity: 'excellent'
          };
        } else if (duration >= 60 && duration < 120) {
          return {
            explanation: `${roundedDuration}s is good for a presentation segment. Consider if more depth would benefit your audience.`,
            tips: ['Add examples or case studies', 'Include audience interaction', 'Expand on key concepts'],
            severity: 'good'
          };
        } else if (duration > 600) {
          return {
            explanation: `${roundedDuration}s is very long for a single presentation segment. Consider breaking into smaller sections.`,
            tips: ['Break into 5-8 minute segments', 'Add audience interaction breaks', 'Use transitions between topics'],
            severity: 'warning'
          };
        } else if (duration > 300) {
          return {
            explanation: `${roundedDuration}s is getting long for sustained attention. Consider adding interaction or breaking into segments.`,
            tips: ['Add audience engagement elements', 'Use visual breaks', 'Consider splitting into sections'],
            severity: 'warning'
          };
        }
      }

      // General duration feedback
      if (duration < 10) {
        return {
          explanation: `${roundedDuration}s is very brief. Consider if you've covered all essential points.`,
          tips: ['Ensure you cover key points', 'Add supporting details', 'Practice pacing'],
          severity: 'poor'
        };
      } else if (duration <= 120) {
        return {
          explanation: `${roundedDuration}s is a good duration for your ${intent === 'other' ? 'practice session' : intent} intent.`,
          tips: ['Maintain this timing consistency', 'Focus on clear delivery'],
          severity: 'good'
        };
      }

      return {
        explanation: `${roundedDuration}s is quite long. Consider if all content is essential.`,
        tips: ['Review for unnecessary content', 'Focus on key messages'],
        severity: 'warning'
      };

    case 'wordsPerMinute':
      if (wordsPerMinute >= 130 && wordsPerMinute <= 150) {
        return {
          explanation: `${wordsPerMinute} words/min is perfect! This is the ideal pace for clear, engaging communication.`,
          tips: ['Maintain this excellent pace', 'Continue practicing consistency', 'Vary pace for emphasis'],
          severity: 'excellent'
        };
      } else if (wordsPerMinute >= 120 && wordsPerMinute <= 170) {
        return {
          explanation: `${wordsPerMinute} words/min is good for clear communication. Well within the optimal range.`,
          tips: ['Maintain this good pace', 'Add strategic pauses for impact'],
          severity: 'good'
        };
      } else if (wordsPerMinute > 200 || (duration < 30 && wordsPerMinute > 180)) {
        return {
          explanation: `${wordsPerMinute} words/min is too fast${duration < 30 ? ' for such a short presentation' : ''}. This pace can overwhelm listeners and reduce comprehension.`,
          tips: ['Slow down significantly to 130-150 words/min', 'Add pauses between key points', 'Practice breathing techniques', 'Focus on articulation'],
          severity: 'poor'
        };
      } else if (wordsPerMinute > 170) {
        return {
          explanation: `${wordsPerMinute} words/min is getting fast. Consider slowing down slightly for better clarity.`,
          tips: ['Aim for 130-150 words/min', 'Add strategic pauses', 'Practice with a metronome'],
          severity: 'warning'
        };
      } else if (wordsPerMinute < 80) {
        return {
          explanation: `${wordsPerMinute} words/min is very slow and may lose listener attention. Consider increasing your pace.`,
          tips: ['Increase to 130-150 words/min', 'Practice reading aloud daily', 'Work on confidence and energy'],
          severity: 'poor'
        };
      } else if (wordsPerMinute < 110) {
        return {
          explanation: `${wordsPerMinute} words/min is somewhat slow. A slightly faster pace would be more engaging.`,
          tips: ['Increase to 130-150 words/min', 'Practice with varied content', 'Focus on maintaining energy'],
          severity: 'warning'
        };
      }
      return {
        explanation: `${wordsPerMinute} words/min is acceptable for speech delivery.`,
        tips: ['Continue working on consistency'],
        severity: 'good'
      };

    case 'sentiment':
      if (value === 'Positive') {
        if (intent === 'sales') {
          return {
            explanation: 'Outstanding! Positive sentiment is crucial for sales success. This enthusiasm will engage prospects and build trust.',
            tips: ['Maintain this fantastic positive energy', 'Balance enthusiasm with credibility', 'Use this positivity to overcome objections'],
            severity: 'excellent'
          };
        } else if (intent === 'interview') {
          return {
            explanation: 'Excellent! Positive sentiment shows enthusiasm and cultural fit. Employers want to hire people who are excited about opportunities.',
            tips: ['Maintain this positive energy throughout', 'Show genuine interest in the role', 'Let your passion for the field shine through'],
            severity: 'excellent'
          };
        } else if (intent === 'presentation') {
          return {
            explanation: 'Great! Positive sentiment engages your audience and makes your content more memorable and persuasive.',
            tips: ['Maintain this engaging positive tone', 'Use this energy to emphasize key points', 'Balance positivity with professionalism'],
            severity: 'excellent'
          };
        }
        return {
          explanation: 'Great! Positive sentiment helps engage your audience and builds rapport.',
          tips: ['Maintain this positive energy', 'Ensure it matches your content tone'],
          severity: 'good'
        };
      } else if (value === 'Neutral') {
        if (intent === 'sales') {
          return {
            explanation: "Neutral sentiment won't close deals. Sales requires enthusiasm and passion to convince prospects of your solution's value.",
            tips: ['Add genuine excitement about your product/service', 'Show passion for solving customer problems', 'Use more dynamic, engaging language', 'Practice with higher energy'],
            severity: 'warning'
          };
        } else if (intent === 'interview') {
          return {
            explanation: 'Neutral sentiment may signal lack of interest to interviewers. Show enthusiasm for the role and company to stand out.',
            tips: ['Express genuine excitement about the opportunity', 'Show passion for your field', "Demonstrate interest in the company's mission", 'Use more engaging, energetic delivery'],
            severity: 'warning'
          };
        } else if (intent === 'presentation') {
          return {
            explanation: 'Neutral sentiment is acceptable for informational presentations, but adding appropriate emotion increases engagement and retention.',
            tips: ['Consider adding enthusiasm for key points', 'Use vocal variety to maintain interest', 'Show passion for your subject matter'],
            severity: 'good'
          };
        }
        return {
          explanation: 'Neutral sentiment is appropriate for informational content, but consider if more emotion would help engage your audience.',
          tips: ['Consider adding appropriate emotion for your topic', 'Use vocal variety to maintain interest'],
          severity: 'good'
        };
      } else if (value === 'Negative') {
        if (intent === 'interview') {
          return {
            explanation: 'Negative sentiment is particularly damaging in interviews. Employers want positive, solution-oriented team members.',
            tips: ['Reframe challenges as learning opportunities', 'Focus on positive outcomes and growth', 'Show optimism about future contributions', 'Practice positive language patterns'],
            severity: 'poor'
          };
        } else if (intent === 'sales') {
          return {
            explanation: 'Negative sentiment kills sales opportunities. Prospects need to feel confident and optimistic about your solution.',
            tips: ['Focus on benefits and positive outcomes', 'Reframe problems as opportunities', 'Use success stories and positive examples', 'Practice enthusiastic delivery'],
            severity: 'poor'
          };
        }
        return {
          explanation: 'Negative sentiment can alienate your audience and reduce message effectiveness. Focus on positive framing and solutions.',
          tips: ['Reframe problems as opportunities', 'Use solution-focused language', 'End with an optimistic outlook', 'Practice positive self-talk before presenting'],
          severity: 'poor'
        };
      }
      return {
        explanation: `${value} sentiment detected in your speech.`,
        tips: ['Consider your audience and adjust tone accordingly'],
        severity: 'good'
      };

    case 'pace':
      if (value === 'Moderate') {
        return {
          explanation: 'Perfect! Moderate pace is ideal for most presentations - it allows for clear understanding while maintaining engagement.',
          tips: ['Maintain this excellent pacing', 'Vary pace for emphasis on key points', 'Use pauses strategically'],
          severity: 'excellent'
        };
      } else if (value === 'Fast') {
        if (duration < 30 || intent === 'educational') {
          return {
            explanation: `Fast pace ${duration < 30 ? 'in such a short presentation' : 'for educational content'} can overwhelm listeners and reduce comprehension.`,
            tips: ['Slow down significantly for better understanding', 'Add strategic pauses between key points', 'Emphasize important words with slower delivery'],
            severity: 'poor'
          };
        } else if (intent === 'interview') {
          return {
            explanation: 'Fast pace in interviews may signal nervousness and make it hard for interviewers to follow your answers.',
            tips: ['Slow down to show confidence and control', 'Take a breath before answering', 'Speak deliberately to emphasize key points', 'Practice relaxation techniques'],
            severity: 'warning'
          };
        } else if (intent === 'presentation') {
          return {
            explanation: 'Fast pace can lose your audience, especially when presenting complex information or to diverse groups.',
            tips: ['Slow down for better audience comprehension', 'Add pauses after important points', 'Check audience understanding', 'Vary pace for emphasis'],
            severity: 'warning'
          };
        }
        return {
          explanation: 'Fast pace can be energetic but may reduce comprehension. Consider slowing down slightly.',
          tips: ['Moderate your pace for clarity', 'Use pauses to let key points sink in', 'Vary your pace for emphasis'],
          severity: 'warning'
        };
      } else if (value === 'Slow') {
        if (intent === 'elevator-pitch' || intent === 'sales') {
          return {
            explanation: `Slow pace may lose listener attention in ${intent === 'sales' ? 'sales pitch' : intent} scenarios where energy and urgency are important.`,
            tips: ['Increase energy and pace', 'Practice with more urgency', 'Focus on delivering key benefits quickly'],
            severity: 'warning'
          };
        } else if (intent === 'interview') {
          return {
            explanation: 'Slow pace in interviews may signal nervousness or lack of confidence. Interviewers prefer clear, confident delivery.',
            tips: ['Practice speaking with more confidence', 'Increase pace slightly while maintaining clarity', 'Show enthusiasm through your delivery speed'],
            severity: 'warning'
          };
        } else if (intent === 'presentation') {
          return {
            explanation: 'Slow pace can work for complex topics but may lose audience attention over time, especially in longer presentations.',
            tips: ['Vary your pace to maintain interest', 'Speed up for exciting points', 'Use slower pace only for emphasis'],
            severity: 'warning'
          };
        }
        return {
          explanation: 'Slow pace can work for detailed explanations but may lose audience engagement over time.',
          tips: ['Increase pace slightly for better engagement', 'Add vocal variety', 'Use energy to maintain interest'],
          severity: 'warning'
        };
      }
      return {
        explanation: `${value} pace detected in your delivery.`,
        tips: ['Adjust pace based on content and audience needs'],
        severity: 'good'
      };

    case 'clarity':
      if (value === 'Clear') {
        return {
          explanation: 'Outstanding! Crystal clear speech ensures your message reaches your audience effectively. This is professional-level clarity.',
          tips: ['Maintain this excellent articulation', 'Continue good microphone technique', 'Use this clarity as your standard'],
          severity: 'excellent'
        };
      } else if (value === 'Mostly Clear' || value === 'Good') {
        return {
          explanation: 'Good clarity overall. Your audience can understand you well with minimal effort.',
          tips: ['Maintain this good articulation', 'Focus on consonant clarity', 'Keep practicing consistent delivery'],
          severity: 'good'
        };
      } else if (value === 'Somewhat Unclear' || value === 'Fair') {
        return {
          explanation: 'Some clarity issues detected. Parts of your message may be difficult for listeners to understand.',
          tips: ['Slow down your speech slightly', 'Focus on articulating consonants', 'Practice speaking closer to the microphone', 'Work on mouth movement exercises'],
          severity: 'warning'
        };
      } else if (value === 'Muffled' || value === 'Unclear' || value === 'Poor') {
        return {
          explanation: 'Significant clarity issues are impacting your message delivery. This can lose your audience and reduce credibility.',
          tips: ['Speak much closer to microphone', 'Articulate each word clearly', 'Slow down significantly', 'Practice tongue twisters daily', 'Consider audio equipment upgrade'],
          severity: 'poor'
        };
      }
      return {
        explanation: `${value} clarity detected in your speech.`,
        tips: ['Focus on clear articulation'],
        severity: 'good'
      };

    case 'fillerWords':
      const fillerRate = fillerCount / Math.max(duration / 60, 1); // fillers per minute
      if (fillerCount === 0) {
        return {
          explanation: 'Outstanding! Zero filler words shows exceptional preparation and confidence. This is professional-level delivery.',
          tips: ['Maintain this excellent professional delivery', 'Continue this high standard', "You're a role model for clear communication"],
          severity: 'excellent'
        };
      } else if (fillerRate <= 1) {
        return {
          explanation: `${fillerCount} filler words (${fillerRate.toFixed(1)}/min) is excellent control. Very professional delivery.`,
          tips: ['Maintain this great filler control', 'Continue practicing strategic pauses', "You're doing very well"],
          severity: 'good'
        };
      } else if (fillerRate <= 2) {
        return {
          explanation: `${fillerCount} filler words (${fillerRate.toFixed(1)}/min) is acceptable but could be improved for more professional delivery.`,
          tips: ['Practice strategic pauses instead of fillers', 'Be comfortable with brief silence', 'Record yourself to identify patterns'],
          severity: 'warning'
        };
      } else if (fillerRate > 3) {
        return {
          explanation: `${fillerCount} filler words in ${roundedDuration}s (${fillerRate.toFixed(1)}/min) is quite high and significantly distracts from your message.`,
          tips: ['Practice pausing instead of using fillers', 'Slow down your speech significantly', 'Prepare and rehearse key phrases', 'Record yourself daily to track progress'],
          severity: 'poor'
        };
      }
      return {
        explanation: `${fillerCount} filler words (${fillerRate.toFixed(1)}/min) needs improvement for professional delivery.`,
        tips: ['Practice strategic pauses', 'Work on being comfortable with silence', 'Prepare key transitions'],
        severity: 'warning'
      };

    default:
      return {
        explanation: 'Analysis completed.',
        tips: ['Keep practicing to improve'],
        severity: 'good'
      };
  }
}

// Helper function to get colors for severity levels
export function getSeverityColors(severity: 'excellent' | 'good' | 'warning' | 'poor') {
  switch (severity) {
    case 'excellent':
      return {
        border: 'border-brand-gold/30',
        background: 'from-brand-gold/10 to-transparent',
        icon: 'text-brand-gold',
        badge: 'bg-brand-gold text-white',
        preview: 'bg-brand-gold/15 text-brand-gold',
        title: 'text-brand-gold'
      };
    case 'good':
      return {
        border: 'border-brand-green/30',
        background: 'from-brand-green/10 to-transparent',
        icon: 'text-brand-green',
        badge: 'bg-brand-green text-brand-white',
        preview: 'bg-brand-green/15 text-brand-green',
        title: 'text-brand-green'
      };
    case 'warning':
      return {
        border: 'border-brand-dark-blue/30',
        background: 'from-brand-dark-blue/10 to-transparent',
        icon: 'text-brand-dark-blue',
        badge: 'bg-brand-dark-blue text-brand-white',
        preview: 'bg-brand-dark-blue/15 text-brand-dark-blue',
        title: 'text-brand-dark-blue'
      };
    case 'poor':
      return {
        border: 'border-brand-error/30',
        background: 'from-brand-error/10 to-transparent',
        icon: 'text-brand-error',
        badge: 'bg-brand-error text-brand-white',
        preview: 'bg-brand-error/15 text-brand-error',
        title: 'text-brand-error'
      };
  }
}

// Mobile device detection
export const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Helper function to get explanation for visual presence metrics
export const getVisualPresenceExplanation = (metric: string, value: any) => {
  switch (metric) {
    case 'focus':
      if (value === 'Focused') {
        return {
          explanation: 'Excellent! You maintained steady eye contact and minimal distractions. This shows strong engagement and professionalism.',
          tips: ['Continue this focused approach', 'Use this as your standard for future presentations'],
          severity: 'excellent' as const
        };
      } else if (value === 'Distracted') {
        return {
          explanation: 'You appeared distracted with frequent eye movement away from the camera. This can reduce audience engagement.',
          tips: ['Practice looking directly at the camera lens', 'Remove distracting elements from your environment', 'Use a teleprompter or notes positioned near the camera'],
          severity: 'warning' as const
        };
      }
      break;
    case 'confidence':
      if (value === 'High') {
        return {
          explanation: 'Outstanding! Your body language projects strong confidence through good posture and steady presence.',
          tips: ['Maintain this confident demeanor', 'Use this confidence to emphasize key points'],
          severity: 'excellent' as const
        };
      } else if (value === 'Low') {
        return {
          explanation: 'Your body language suggests nervousness or uncertainty. This can undermine your message credibility.',
          tips: ['Practice power poses before presenting', 'Stand or sit up straighter', 'Slow down your movements', 'Practice deep breathing exercises'],
          severity: 'warning' as const
        };
      }
      break;
    case 'tension':
      if (value === 'Low') {
        return {
          explanation: 'Perfect! You appear relaxed and natural, which helps your audience feel comfortable and engaged.',
          tips: ['Maintain this relaxed approach', 'Use this natural state to connect with your audience'],
          severity: 'excellent' as const
        };
      } else if (value === 'High') {
        return {
          explanation: 'You appear tense or stressed, which can make your audience uncomfortable and distract from your message.',
          tips: ['Practice relaxation techniques before presenting', 'Do shoulder rolls and neck stretches', 'Focus on breathing deeply', 'Practice your content more to build confidence'],
          severity: 'warning' as const
        };
      }
      break;
  }
  return {
    explanation: `${metric} level detected in your presentation.`,
    tips: ['Continue working on your visual presence'],
    severity: 'good' as const
  };
};

export const getScoreExplanation = (metric: string, score: number) => {
  const percentage = Math.round(score);
  switch (metric) {
    case 'posture':
      if (score >= 80) {
        return {
          explanation: `Excellent posture (${percentage}%)! You maintain professional, upright positioning that projects confidence and authority.`,
          tips: ['Keep maintaining this excellent posture', 'Use your good posture to emphasize important points'],
          severity: 'excellent' as const
        };
      } else if (score >= 60) {
        return {
          explanation: `Good posture (${percentage}%) with room for improvement. Your positioning is generally professional but could be more consistent.`,
          tips: ['Focus on keeping shoulders back and spine straight', 'Practice sitting/standing tall throughout your presentation', 'Use a mirror to check your posture while practicing'],
          severity: 'good' as const
        };
      } else {
        return {
          explanation: `Poor posture (${percentage}%) is significantly impacting your professional appearance and may distract from your message.`,
          tips: ['Practice proper sitting/standing posture daily', 'Set up your camera at eye level', 'Do posture exercises before presenting', 'Consider ergonomic adjustments to your setup'],
          severity: 'poor' as const
        };
      }
    case 'movement':
      if (score >= 80) {
        return {
          explanation: `Excellent movement stability (${percentage}%)! Your movements are controlled and purposeful, enhancing your message delivery.`,
          tips: ['Continue using deliberate, controlled movements', 'Use your stable presence to build audience trust'],
          severity: 'excellent' as const
        };
      } else if (score >= 60) {
        return {
          explanation: `Good movement stability (${percentage}%) but some unnecessary motion detected. Reducing fidgeting will improve your professional appearance.`,
          tips: ['Practice standing or sitting still during key points', 'Use purposeful gestures only', 'Record yourself to identify unconscious movements'],
          severity: 'good' as const
        };
      } else {
        return {
          explanation: `Poor movement stability (${percentage}%) with excessive fidgeting or swaying that distracts from your message.`,
          tips: ['Practice staying still and centered', 'Focus on controlled, intentional movements only', 'Work on nervous energy management', 'Practice relaxation techniques'],
          severity: 'poor' as const
        };
      }
    case 'presence':
      if (score >= 80) {
        return {
          explanation: `Outstanding overall presence (${percentage}%)! You command attention and project strong leadership qualities through your visual presentation.`,
          tips: ['Maintain this excellent commanding presence', 'Use your strong presence to influence and persuade'],
          severity: 'excellent' as const
        };
      } else if (score >= 60) {
        return {
          explanation: `Good overall presence (${percentage}%) with solid fundamentals. Refining a few areas will elevate your impact significantly.`,
          tips: ['Work on the specific areas identified above', 'Practice presenting with more energy and intention', 'Focus on projecting confidence through body language'],
          severity: 'good' as const
        };
      } else {
        return {
          explanation: `Weak overall presence (${percentage}%) may undermine your credibility and message effectiveness.`,
          tips: ['Focus on improving posture and reducing nervous movements', 'Practice presenting with more confidence and energy', 'Work on eye contact and facial expressions', 'Consider presentation skills training'],
          severity: 'poor' as const
        };
      }
  }
  return {
    explanation: `${metric} score: ${percentage}%`,
    tips: ['Continue working on improvement'],
    severity: 'good' as const
  };
};
